import { Cheerio, load } from 'cheerio';
import { Document, Element, Node, NodeWithChildren, ParentNode, Text } from 'domhandler';

/**
 * Parses a piece of Confluence storage format content using cheerio and returns a cheerio element containing the content.
 * The reason why cheerio is used (for now) rather than the browser's own DOMParser is to keep consistency between the
 * frontend and the backend, and to make sure that the peculiarities of the Confluence storage format are dealt with:
 * - HTML entities are supported. The browser supports these when an XHTML DOCTYPE is declared, but many JavaScript
 *   XML parsers show errors when encountering these (including the DOMParser provided by jsdom, which is used in unit
 *   tests).
 * - CDATA sections are parsed correctly and preserved when serializing the XHTML. Confluence requires a CDATA section
 *   within ac:plain-text-body elements (and possibly elsewhere). Many JavaScript HTML parsers don't support these.
 * - Namespaces are handled properly and consistently. Confluence storage format uses XML elements and attributes with
 *   a ac:, ri: and at: prefixes in addition to regular XHTML. JQuery and browsers have inconsistent behaviour when
 *   using query selectors with namespaces, where in some cases an ac:link element has to be queried as "ac\:link" and
 *   in others as "link" (see https://bugs.jquery.com/ticket/4208).
 */
export function parseStorageFormat(xhtml: string): Cheerio<Element> {
  // See https://ecosystem.atlassian.net/browse/CE-556?focusedCommentId=268646
  const doc = `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" xmlns:atlassian-content="http://atlassian.com/content" xmlns:ac="http://atlassian.com/content" xmlns:ri="http://atlassian.com/resource/identifier" xmlns:atlassian-template="http://atlassian.com/template" xmlns:at="http://atlassian.com/template"><body>${xhtml}</body></html>`;

  const $ = load(doc, {
    // See https://github.com/cheeriojs/cheerio/issues/2183#issuecomment-952419972
    xml: {
      xmlMode: false,
      recognizeCDATA: true,
      recognizeSelfClosing: true,
      selfClosingTags: true,
      emptyAttrs: true,
    },
  } as any);
  return $('body');
}

export function parseHTML(html: string): Cheerio<Document> {
  const $ = load(html, undefined, false);
  return $.root();
}

/**
 * Parses a fragment of HTML produced by Confluence, for example from a conversion to the export view. Confluence produces
 * HTML that uses self-closing tags (such as <div/>) without being valid XHTML. Browsers would interpret these as opening
 * tags instead, which is not what we want.
 */
export function parseConfluenceHTML(html: string): Cheerio<Document> {
  const $ = load(
    html,
    {
      xml: {
        xmlMode: false,
        recognizeCDATA: true,
        recognizeSelfClosing: true,
        selfClosingTags: true,
        emptyAttrs: true,
      },
    } as any,
    false
  );
  return $.root();
}

/**
 * Given a cheerio set of elements, iterates over the individual elements, yielding a cheerio set containing exactly
 * one element for each element in the set. This makes it possible to use cheerio methods on each of the elements
 * in the set (see https://github.com/cheeriojs/cheerio/discussions/2187).
 */
export function* cheerioIterator<T>(el: Cheerio<T>): Generator<Cheerio<T>, void, void> {
  for (let i = 0; i < el.length; i++) {
    yield el.eq(i);
  }
}

/**
 * Iterates through the descendant nodes of the given cheerio element, yielding each node when it is opened and when it is
 * closed. For childless nodes (such as text nodes), an open and close object are emitted right after each other.
 */
export function* domTreeIterator(
  el: Cheerio<ParentNode>
): Generator<{ type: 'open' | 'close'; node: Node }, void, void> {
  const stack: Node[] = [el[0]];
  outer: while (stack.length > 0) {
    const cur = stack[stack.length - 1];
    yield { type: 'open', node: cur };
    if (cur instanceof NodeWithChildren && cur.firstChild) {
      stack.push(cur.firstChild);
    } else {
      while (
        !stack[stack.length - 1].nextSibling ||
        /* Cancel when reaching el */ stack.length === 1
      ) {
        yield { type: 'close', node: stack.pop()! };
        if (stack.length === 0) {
          break outer;
        }
      }
      yield { type: 'close', node: stack[stack.length - 1] };
      stack[stack.length - 1] = stack[stack.length - 1].nextSibling!;
    }
  }
}

/**
 * Replace all whitespace, line breaks and tab characters with a single space.
 * @param node The node to replace the value of.
 * @param trim If true, the result is trimmed.
 * @returns The text content of the given node, or undefined if the node is a comment or a processing instruction.
 */
export function replaceBlankCharacters(
  node: Node | Text,
  trim = true
): string | undefined {
  const replaceValue =
    node instanceof Text ? node.nodeValue.replace(/[\r\n\t ]+/g, ' ') : undefined;
  return replaceValue && trim ? replaceValue?.trim() : replaceValue;
}

/**
 * Returns the text content of the given cheerio element, making a best attempt to represent line breaks caused by
 * block elements and paragraphs in the given data.
 */
export function getTextContent(
  el: Cheerio<ParentNode>,
  getText = (node: Node) => replaceBlankCharacters(node)
): string {
  let result = '';
  let currentPrefix = '';
  for (const { type, node } of domTreeIterator(el)) {
    if (node instanceof Element) {
      if (node.tagName === 'p') {
        if (type === 'open') {
          result += '\n';
        }
        currentPrefix = '\n';
      } else if (node.tagName === 'br' && type === 'open') {
        result += '\n';
        currentPrefix = '';
      } else if (
        !result.endsWith('\n') &&
        [
          'address',
          'article',
          'aside',
          'blockquote',
          'details',
          'dialog',
          'dd',
          'dl',
          'div',
          'dt',
          'fieldset',
          'figcaption',
          'figure',
          'footer',
          'form',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'header',
          'hgroup',
          'hr',
          'li',
          'main',
          'nav',
          'ol',
          'pre',
          'section',
          'table',
          'ul',
        ].includes(node.tagName)
      ) {
        currentPrefix = '\n';
      } else if (
        !result.endsWith('\n') &&
        !result.endsWith(' ') &&
        ['td', 'th'].includes(node.tagName) &&
        currentPrefix === ''
      ) {
        currentPrefix = ' ';
      }
    }

    if (type === 'open') {
      const text = getText(node);
      if (text) {
        result += currentPrefix;
        currentPrefix = '';
        result += text;
      }
    }
  }
  return result.trim();
}
